import { delay, put, putResolve, select } from "redux-saga/effects";
import { PRICE_QUOTE_HOTEL, HotelPriceQuoteWithAncillaries } from "redmond";
import { isCorpTenant, DO_NOT_APPLY_REWARDS_KEY } from "@capone/common";
import dayjs from "dayjs";
import {
  ErrorCode,
  PaymentError,
  ProductError,
  PurchaseError,
  PurchaseErrorEnum,
  QuoteFailure,
  QuoteResponseEnum,
  QuoteSuccessV0,
  QuoteResponseV0,
  QuoteResponse,
} from "@b2bportal/purchase-api";
import queryStringParser from "query-string";
import { IStoreState } from "../../../../reducers/types";
import {
  getOpaquePayments,
  getPriceQuoteHotelProperties,
  getPricingEstimateTotal,
  getSession,
  getIsReshopQuotePending,
  getIsConnectionRetryPending,
} from "../../reducer";
import {
  setPollPriceQuoteCallStateFailure,
  setPollPriceQuoteCallStateSuccess,
  setPriceQuote,
} from "../../actions/actions";
import { actions } from "../../actions";
import { getPurchaseApis } from "../../utils";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { config } from "../../../../api/config";

const toErrorString = (error: PurchaseError): string => {
  switch (error.Error) {
    case PurchaseErrorEnum.ErrorCode:
      return (error as ErrorCode).code;
    case PurchaseErrorEnum.PaymentError:
      const paymentError = error as PaymentError;
      return JSON.stringify(paymentError.value.value);
    case PurchaseErrorEnum.ProductError:
      const productError = error as ProductError;
      return JSON.stringify(productError.value.value);
    default:
      return PurchaseErrorEnum[error.Error];
  }
};

const defaultRewardsAccountRefId = isCorpTenant(config.TENANT)
  ? DO_NOT_APPLY_REWARDS_KEY
  : null;

export function* pollPriceQuoteSaga({
  agentFee,
  pollQuoteOnly,
  ancillaries,
  history,
}: actions.IPollPriceQuote) {
  try {
    const state: IStoreState = yield select();
    const sessionToken = getSession(state);
    const { pollPriceQuote } = getPurchaseApis();
    const pollQuoteHotelProperties = getPriceQuoteHotelProperties(state);
    const startTime = dayjs();

    if (!sessionToken) {
      throw new Error("Session token is not present.");
    }

    const delayTimes = [1000];

    let pollFailed = false;
    let index = 0;
    while (!pollFailed) {
      yield delay(delayTimes[index]);
      const priceQuoteCheckedResponse: QuoteResponse | QuoteResponseV0 =
        yield pollPriceQuote({
          req: sessionToken,
        });

      switch (priceQuoteCheckedResponse.QuoteResponse) {
        case QuoteResponseEnum.Failure:
          pollFailed = true;
          const failedResponse = priceQuoteCheckedResponse as QuoteFailure;
          if (failedResponse.errors.length > 0) {
            yield putResolve(
              setPollPriceQuoteCallStateFailure(failedResponse.errors)
            );
            trackEvent({
              eventName: PRICE_QUOTE_HOTEL,
              properties: {
                ...pollQuoteHotelProperties.properties,
                failure_reason:
                  failedResponse.errors.length > 0
                    ? failedResponse.errors
                        .map((error) => toErrorString(error))
                        .join(" - ")
                    : "Poll quote checked response returned an error and the given error code is not handleable.",
              },
              encryptedProperties: [
                ...pollQuoteHotelProperties.encryptedProperties,
              ],
            });
            return;
          }
          trackEvent({
            eventName: PRICE_QUOTE_HOTEL,
            properties: {
              ...pollQuoteHotelProperties.properties,
              failure_reason:
                "Poll quote checked response returned an error and the given error code is not handleable.",
            },
            encryptedProperties: [
              ...pollQuoteHotelProperties.encryptedProperties,
            ],
          });
          yield putResolve(setPollPriceQuoteCallStateFailure([]));
          throw new Error(
            "Price quote checked response returned an error and the given error code is not handleable."
          );

        case QuoteResponseEnum.Pending:
          break;
        case QuoteResponseEnum.Success:
          const priceQuoteResponse =
            priceQuoteCheckedResponse as QuoteSuccessV0;
          // BE returns a HotelPriceQuoteWithAncillaries object
          // If hotels cfar is not enabled, the hotelAncillaryQuotes and combinedPricing fields will be empty
          const hotelPriceQuoteWithAncillaries = priceQuoteResponse.quote
            .value as HotelPriceQuoteWithAncillaries;

          if (hotelPriceQuoteWithAncillaries) {
            yield putResolve(setPollPriceQuoteCallStateSuccess());
            yield putResolve(
              setPriceQuote({
                priceQuote: hotelPriceQuoteWithAncillaries.hotelQuoteData,
                pricingWithAncillaries:
                  hotelPriceQuoteWithAncillaries.combinedPricing,
                hotelAncillaryQuotes:
                  hotelPriceQuoteWithAncillaries.hotelAncillaryQuotes ?? null,
                type: "hotel-checkout",
                // Reshop + connection-retry (CT1261T-419) re-quotes keep the chosen
                // payment so the customer isn't left behind a disabled book button.
                preservePaymentSelection:
                  getIsReshopQuotePending(state) ||
                  getIsConnectionRetryPending(state),
              })
            );

            const estimate = getPricingEstimateTotal(state);

            trackEvent({
              eventName: PRICE_QUOTE_HOTEL,
              properties: {
                ...pollQuoteHotelProperties.properties,
                success: true,
                load_time: dayjs().diff(startTime, "seconds", true),
              },
              encryptedProperties: [
                ...pollQuoteHotelProperties.encryptedProperties,
              ],
            });
            if (pollQuoteOnly) {
              return;
            }

            // TODO: When the BE fulfill endpoint is ready, this should be refactored
            // to use the getPriceQuotePricing selector to instead of
            // hotelPriceQuoteWithAncillaries.hotelQuoteData.pricing
            if (
              estimate?.fiat.value ===
              hotelPriceQuoteWithAncillaries.hotelQuoteData.pricing.grandTotal
                .amount
            ) {
              const paymentRequest = getOpaquePayments(state);
              if (paymentRequest) {
                const queryString = history
                  ? queryStringParser.parse(history.location.search)
                  : {};
                yield put(
                  actions.scheduleBook({
                    agentFee,
                    isRecommended: queryString.recommended === "true",
                    ancillaries,
                    initialTime: dayjs().toISOString(),
                  })
                );
              } else {
                yield putResolve(setPollPriceQuoteCallStateFailure([]));
                throw new Error("Payment amount is undefined");
              }
            } else {
              yield put(
                actions.setSelectedPaymentMethodId({
                  paymentMethodId: "",
                  accountId: undefined,
                })
              );
              yield put(
                actions.setSelectedRewardsAccountReferenceId(
                  defaultRewardsAccountRefId
                )
              );
            }
            return;
          }
          trackEvent({
            eventName: PRICE_QUOTE_HOTEL,
            properties: {
              ...pollQuoteHotelProperties.properties,
              failure_reason: "Poll quote failed",
            },
            encryptedProperties: [
              ...pollQuoteHotelProperties.encryptedProperties,
            ],
          });
          pollFailed = true;
          yield putResolve(setPollPriceQuoteCallStateFailure([]));
          throw new Error("Price Quote Failed");
      }
      if (index !== delayTimes.length - 1) {
        index++;
      }
    }
  } catch (e) {
    const state: IStoreState = yield select();
    const pollQuoteHotelProperties = getPriceQuoteHotelProperties(state);
    trackEvent({
      eventName: PRICE_QUOTE_HOTEL,
      properties: {
        ...pollQuoteHotelProperties.properties,
        failure_reason: "Poll quote request failed",
      },
      encryptedProperties: [...pollQuoteHotelProperties.encryptedProperties],
    });
    yield putResolve(setPollPriceQuoteCallStateFailure([]));
  }
}
